import {
  convertToSLSRectangle,
  getMostRecentFieldValue,
  getNestedOrFloatingElementById,
  getNextUuid,
} from '@fs/zion-sls'

/**
 * An array of name field types used in the Full Name Review app.
 *
 * @type {string[]}
 */
export const nameFieldTypes = ['NAME_PRE', 'NAME_GN', 'NAME_SPRE', 'NAME_SURN', 'NAME_SUF', 'NAME_PATR', 'NAME_MATR']

/**
 * Checks if all tasks are completed already.
 *
 * @param {Array} nameElements - Array of name elements.
 * @returns {boolean} - True if all tasks are completed, otherwise false.
 */
export const allTasksCompletedAlready = (nameElements) => {
  return nameElements.every((nameElement) => nameElement.attribution?.userId?.startsWith('cis'))
}

/**
 * Confirms if changes have been made to the name values.
 *
 * @param {Object} params
 * @param {Object} params.nameValues - Name values object.
 * @param {Array} params.images - Array of images.
 * @returns {boolean} - True if changes have been made, otherwise false.
 */
export const confirmThatChangesHaveBeenMade = ({ nameValues, images }) => {
  let nameValuesAreDifferent = false
  if (typeof nameValues === 'object') {
    Object.entries(nameValues).forEach(([nameKey, nameValue]) => {
      if (nameValue && Array.isArray(nameValue?.fieldIds)) {
        nameValue.fieldIds.forEach((fieldId) => {
          const slsFieldObject = getNestedOrFloatingElementById(images, fieldId)
          if (!slsFieldObject) return

          // Get these from the SLS data that has yet to be modified
          const oldFieldValue = getMostRecentFieldValue(slsFieldObject)
          const oldFieldType = slsFieldObject.fieldType

          // Get these from the UI state representing the same values as above, but with the potential to have been modified by the user
          const newFieldValue = nameValue.text
          const newFieldType = nameKey

          if (oldFieldValue !== newFieldValue || oldFieldType !== newFieldType) nameValuesAreDifferent = true
        })
      }
    })
  }
  return nameValuesAreDifferent
}

/**
 * Updates the current name parts state after modyfing higlights on step 2.
 *
 * @param {Object} params
 * @param {Array} params.mainHighlights - Array of highlights for the image.
 * @param {Object} params.state - Full name state object.
 * @param {Object} params.zionSlsState - Zion SLS state object.
 * @param {Function} params.t - Translation function.
 * @param {Function} params.dispatch - Full Name Reducer dispatch function.
 */
export const updateCurrentNameParts = ({ mainHighlights, state, zionSlsState, t, dispatch }) => {
  const highlightsStolenFromExistingFields = []
  // Update the stepThree state object based on the changes made to highlights in Step Two
  const updatedCurrentNameParts = {}
  const allStepThreeFields = Object.values(state.currentNameParts).reduce((acc, fieldArrays) => {
    Object.values(fieldArrays).forEach((fieldArray) => {
      acc = acc.concat(fieldArray)
    })
    return acc
  }, [])

  const attachedHighlights = mainHighlights.filter((hl) => hl.attached)

  attachedHighlights.forEach((highlight) => {
    // We only need to create a new entry for a highlight if there isn't one already in stepThree
    const fieldInStepThreeForHighlight = allStepThreeFields.find(
      (field) => field.fieldId === highlight.originalHighlight.fieldId
    )
    // If the highlight is linked to an existing field, then we want to use that information
    const fieldInSLSForHighlight = getNestedOrFloatingElementById(
      zionSlsState.images,
      highlight.originalHighlight.fieldId
    )
    let fieldType = 'NAME_GN'
    // If the field associated with the highlight exists in the stepThree object, use data from that field to populate this one
    if (fieldInStepThreeForHighlight) {
      if (!updatedCurrentNameParts[fieldInStepThreeForHighlight.fieldType])
        updatedCurrentNameParts[fieldInStepThreeForHighlight.fieldType] = { fields: [] }
      updatedCurrentNameParts[fieldInStepThreeForHighlight.fieldType].fields.push({
        elementType: fieldInStepThreeForHighlight.elementType,
        partitionKey: fieldInStepThreeForHighlight.partitionKey,
        fieldId: fieldInStepThreeForHighlight.id,
        superElements: fieldInStepThreeForHighlight.superElements,
        fieldType: fieldInStepThreeForHighlight.fieldType,
        id: highlight.id,
        rects: [convertToSLSRectangle(highlight)],
        typeData: fieldInStepThreeForHighlight.typeData,
        confidence: fieldInStepThreeForHighlight.confidence,
        userEnteredValue: updatedCurrentNameParts[fieldInStepThreeForHighlight.fieldType].fields.find(
          (field) => field.fieldId === fieldInStepThreeForHighlight.id
        )
          ? ''
          : fieldInStepThreeForHighlight.userEnteredValue,
      })
    } else if (fieldInSLSForHighlight) {
      // If the type of existingFieldForHighlight is a name field type, we can use it as is
      if (nameFieldTypes.includes(fieldInSLSForHighlight.fieldType)) {
        fieldType = fieldInSLSForHighlight.fieldType
      }
      if (!updatedCurrentNameParts[fieldType]) updatedCurrentNameParts[fieldType] = { fields: [] }
      updatedCurrentNameParts[fieldType].fields.push({
        elementType: fieldInSLSForHighlight.elementType,
        partitionKey: fieldInSLSForHighlight.partitionKey,
        fieldId: fieldInSLSForHighlight.id,
        superElements: fieldInSLSForHighlight.superElements,
        fieldType,
        id: highlight?.id,
        rects: [convertToSLSRectangle(highlight)],
        typeData: fieldInSLSForHighlight.typeData,
        confidence: fieldInSLSForHighlight.confidence,
        userEnteredValue: updatedCurrentNameParts[fieldType].fields.find(
          (field) => field.fieldId === fieldInSLSForHighlight.id
        )
          ? ''
          : getMostRecentFieldValue(fieldInSLSForHighlight),
      })
    } else {
      // If the highlight existed before this task and was linked to another field not on this task, it will have this change
      if (highlight.change && highlight.change === 'edit') highlightsStolenFromExistingFields.push(highlight)
      // For all cases where the highlight is not linked to a field on the task, we create data representing a new field
      if (!updatedCurrentNameParts[fieldType]) updatedCurrentNameParts[fieldType] = { fields: [] }
      updatedCurrentNameParts[fieldType].fields.push({
        elementType: 'FIELD',
        partitionKey: allStepThreeFields[0].partitionKey,
        fieldId: getNextUuid(),
        superElements: [],
        fieldType,
        id: highlight.id,
        rects: [convertToSLSRectangle(highlight)],
        typeData: {
          displayLabel: t('full-name-correction.name-input.placeholder-text', 'Name'),
        },
        confidence: '',
        userEnteredValue: '',
      })
    }

    dispatch({ type: 'SET_CURRENT_NAME_PARTS', value: updatedCurrentNameParts })
  })
}
